import { useState, useEffect, useRef } from 'react';
import { categoriesApi } from '../api';
import { Button, Input, Modal, Spinner, EmptyState } from '../components/ui';

const PRESET_COLORS = ['#DC4C3E','#D97706','#16A34A','#0D9488','#4F6EF7','#7C3AED','#EC4899','#64748B'];
const PRESET_ICONS  = ['💼','🏠','❤️','📚','🎯','🏋️','✈️','💡','🎨','🍎','💰','🌿'];

function CategoryCard({ cat, onEdit, onDelete }) {
  const [deleting, setDeleting] = useState(false);
  const [hovering, setHovering] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try { await onDelete(cat.id); } catch { setDeleting(false); }
  };

  return (
    <div
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      style={{
        padding: '13px 16px',
        display: 'flex', alignItems: 'center', gap: 12,
        background: hovering ? 'var(--hover)' : 'transparent',
        transition: 'background 0.1s',
      }}
    >
      <div style={{
        width: 36, height: 36, borderRadius: 8, flexShrink: 0,
        background: cat.color + '18',
        border: `1.5px solid ${cat.color}40`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 18,
      }}>
        {cat.icon || '🏷️'}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{cat.name}</div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 1 }}>
          {cat.tasks_count ?? 0} tasks · {cat.done_count ?? 0} done
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, flexShrink: 0, opacity: hovering ? 1 : 0.5, transition: 'opacity 0.15s' }}>
        <button onClick={() => onEdit(cat)} style={{
          background: 'var(--hover)', border: '1px solid var(--border)',
          borderRadius: 6, padding: '5px 10px', cursor: 'pointer',
          fontSize: 12, fontWeight: 500, color: 'var(--text2)', fontFamily: 'inherit',
        }}>Edit</button>
        <button onClick={handleDelete} disabled={deleting} style={{
          background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)',
          borderRadius: 6, padding: '5px 10px', cursor: 'pointer',
          fontSize: 12, fontWeight: 500, color: '#EF4444', fontFamily: 'inherit',
          opacity: deleting ? 0.5 : 1,
        }}>{deleting ? '…' : 'Delete'}</button>
      </div>
    </div>
  );
}

function CategoryModal({ open, onClose, onSave, cat }) {
  const [form,   setForm]   = useState({ name: '', color: '#DC4C3E', icon: '' });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  useEffect(() => {
    setForm(cat
      ? { name: cat.name, color: cat.color, icon: cat.icon || '' }
      : { name: '', color: '#DC4C3E', icon: '' }
    );
    setError('');
  }, [cat, open]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    try { await onSave(form); onClose(); }
    catch { setError('Failed to save. Try again.'); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={cat ? 'Edit category' : 'New category'}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Input
          label="Name" placeholder="e.g. Work"
          value={form.name}
          onChange={e => { set('name', e.target.value); setError(''); }}
          error={error}
          autoFocus
        />

        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 8 }}>Color</label>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            {PRESET_COLORS.map(c => (
              <div key={c} onClick={() => set('color', c)} style={{
                width: 26, height: 26, borderRadius: 7, background: c, cursor: 'pointer',
                outline: form.color === c ? `2px solid ${c}` : 'none',
                outlineOffset: 2, transform: form.color === c ? 'scale(1.12)' : 'scale(1)',
                transition: 'all 0.1s',
              }} />
            ))}
          </div>
        </div>

        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 8 }}>Icon</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {PRESET_ICONS.map(ic => (
              <div key={ic} onClick={() => set('icon', ic === form.icon ? '' : ic)} style={{
                width: 34, height: 34, borderRadius: 7, fontSize: 16, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: form.icon === ic ? 'var(--accent-light)' : 'var(--hover)',
                border: `1px solid ${form.icon === ic ? 'var(--accent)' : 'var(--border)'}`,
                transition: 'all 0.1s',
              }}>{ic}</div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 8, borderTop: '1px solid var(--border)' }}>
          <Button variant="ghost" onClick={onClose} size="sm">Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !form.name.trim()} size="sm">
            {saving ? 'Saving…' : cat ? 'Save changes' : 'Create'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modalOpen,  setModalOpen]  = useState(false);
  const [editCat,    setEditCat]    = useState(null);

  // Single fetch — fetchingRef guards against StrictMode double-invoke
  const fetchingRef = useRef(false);
  useEffect(() => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    categoriesApi.list()
      .then(r => setCategories(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (data) => {
    if (editCat) {
      const { data: updated } = await categoriesApi.update(editCat.id, data);
      setCategories(prev => prev.map(c => c.id === editCat.id ? updated : c));
    } else {
      const { data: created } = await categoriesApi.create(data);
      setCategories(prev => [...prev, created]);
    }
  };

  const handleDelete = async (id) => {
    await categoriesApi.remove(id);
    setCategories(prev => prev.filter(c => c.id !== id));
  };

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}><Spinner size={28} /></div>;
  }

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', padding: 'clamp(16px,4vw,32px) clamp(12px,4vw,24px) 80px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22, gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.5px' }}>Categories</h1>
          <p style={{ fontSize: 13, color: 'var(--text3)', marginTop: 2 }}>Organise your tasks.</p>
        </div>
        <Button onClick={() => { setEditCat(null); setModalOpen(true); }} size="sm" style={{ flexShrink: 0 }}>
          + New
        </Button>
      </div>

      {categories.length === 0
        ? <EmptyState icon="🏷️" title="No categories yet" sub="Create one to organise your tasks." />
        : (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            {categories.map((c, i) => (
              <div key={c.id} style={{ borderBottom: i < categories.length - 1 ? '1px solid var(--divider)' : 'none' }}>
                <CategoryCard cat={c}
                  onEdit={cat => { setEditCat(cat); setModalOpen(true); }}
                  onDelete={handleDelete}
                />
              </div>
            ))}
          </div>
        )
      }

      <CategoryModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditCat(null); }}
        onSave={handleSave}
        cat={editCat}
      />
    </div>
  );
}